import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { t_DataIdentifier } from '../did-data-retrieval/did-data-retrieval.service';
import { t_Did, DidConfigService } from '../did-config/did-config.service';
import { _baseUrl, _sentrackBaseUrl, GlobalService } from '../../helper/global-helper';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
    providedIn: 'root',
})
export class BasicMachineFunctionsService {
    private baseUrl: string;
    private sentrackUrl: string;
    public basicMachineFunctionsConfig: t_BasicMachineFunctionsConfig;
    private loadedFunctionSubject = new BehaviorSubject<string>('');
    public loadedFunctionObservable: Observable<string> = this.loadedFunctionSubject.asObservable();
    private token: string | undefined;
    private machinenumber: string = "";
    private line: string = '';
    dataEcuIds: number[] = [];

    constructor(private http: HttpClient, public didConfigConnection: DidConfigService, private keycloak: KeycloakService, private global: GlobalService) {
        this.baseUrl = _baseUrl;
        this.sentrackUrl = _sentrackBaseUrl;
        this.basicMachineFunctionsConfig = { functions: [] };
        this.global.machineNumber.subscribe({
            next: newValue => {this.machinenumber = newValue.number;}
        });
        this.global.machineInfos.subscribe({
            next: newValue => {this.line = newValue.line;}
        });
    }

    public initService(line: string){
        this.loadBasicMachineFunctionConfig(line);
    }

    // Load Basic Machine Function Config from Backend and store in Service Property - add a checked flag on storing
    public loadBasicMachineFunctionConfig(line: string): void {
        if (this.basicMachineFunctionsConfig.functions.length != 0) {
            //console.log('Config Already Loaded');
        } else {
            this.http
                .get<t_BasicMachineFunctionsConfig>(this.baseUrl + 'basicfunctionsconfig?productline=' + line + 'line', {
                    observe: 'body',
                    responseType: 'json',
                })
                .subscribe({
                    next: (response) => {
                        response.functions.forEach((func) => {
                            this.basicMachineFunctionsConfig.functions.push({
                                name: func.name,
                                dataNames: func.dataNames,
                                identifierLoaded: false,
                            });
                        });
                    }
                });
        }
    }

    /**
     * *Workflow*
     * - Get DataNames of Function
     * - Get Ecu IDs of DataNames
     * - For each EcuID :
     *      - If not loaded, load DidConfig of Ecu ID => Function in DID Config
     *      - Then:
     *          - Search for DataName in ECU Set -> Get Data Id
     *          - Add ECU ID and Data ID as Dataidentifier for dataname in passed reference!
     * @param functionName Name of the main machine function to set the data identifiers for
     */
    public async setDataIdentifiersOfFunction(functionName: string): Promise<void> {
        let functionConfigIndex: number;
        if(functionName == 'AllValues') {
            functionConfigIndex = this.basicMachineFunctionsConfig.functions.length
            await this.addGeneralMappingToBasicConfig();            
        } else {
            functionConfigIndex = this.getIndexOfFunctionName(functionName);
            this.dataEcuIds = this.getEcuIdListOfDataNames(this.basicMachineFunctionsConfig.functions[functionConfigIndex].dataNames);

            this.basicMachineFunctionsConfig.functions[functionConfigIndex].dataNames.forEach((dataName) => {
                dataName.identifier = this.didConfigConnection.getDataIdentifierOfDataName(dataName.name);
                dataName.checked = false;
            });
            this.basicMachineFunctionsConfig.functions[functionConfigIndex].dataNames = this.basicMachineFunctionsConfig.functions[functionConfigIndex].dataNames
                .map((dataName) => dataName)
                .filter((dataName) => dataName.identifier?.ecu_id !== undefined);
            this.loadedFunctionSubject.next(this.basicMachineFunctionsConfig.functions[functionConfigIndex].name);
        }
    }

    private addGeneralMappingToBasicConfig(): Promise<void> {
        return new Promise((resolve) => {
            this.didConfigConnection.getEcuList(this.machinenumber, this.line).subscribe((res) => {
                res.forEach((ecu) => {
                    this.dataEcuIds.push(ecu.ecu_id);
                    this.didConfigConnection.getSpecificEcuDidMapping(this.machinenumber, ecu.ecu_id, this.line).subscribe((resp) => {
                        let data = JSON.parse(JSON.stringify(resp[0].ecu_dids));
                        let names: t_dataName[] = [];
                        data.forEach((set:any) => {
                            names.push({name: set.did_name, identifier: {ecu_id: ecu.ecu_id, data_id: set.did}, didInformation: set});
                        });
                        let index = this.basicMachineFunctionsConfig.functions.findIndex((el) => el.name == 'AllValues');
                        if(index !== -1) {
                            this.basicMachineFunctionsConfig.functions[index].dataNames.push(...names);
                        } else {
                            this.basicMachineFunctionsConfig.functions.push({name: 'AllValues', dataNames: names, identifierLoaded: false});
                        }
                    });
                });
            });
            resolve();
        });
    }

    public removeGeneralMappingFromBasicConfig() {
        if(this.basicMachineFunctionsConfig.functions.findIndex((el) => el.name == 'AllValues') != -1){
            this.basicMachineFunctionsConfig.functions.pop();
        }
    }

    public getIndexOfFunctionName(functionName: string): number {
        const functionConfigIndex = this.basicMachineFunctionsConfig.functions.findIndex((func) => func.name === functionName);
        return functionConfigIndex;
    }

    private getIndexOfDataIdentifierInFunction(functionIndex: number, identifier: t_DataIdentifier): number {
        return this.basicMachineFunctionsConfig.functions[functionIndex].dataNames.findIndex((dataName) => {            
            return dataName.identifier?.ecu_id == identifier.ecu_id && dataName.identifier?.data_id == identifier.data_id;
        });
    }

    private getEcuIdListOfDataNames(dataNames: t_dataName[]): number[] {
        let response: number[] = [];
        try {
            dataNames.forEach((dataName) => {
                let dataNameEcuId: number = 0;
                dataNameEcuId = this.didConfigConnection.getEcuId(dataName.name);
                if (!response.includes(dataNameEcuId)) {
                    response.push(dataNameEcuId);
                }
            });
        } catch (error) {
            console.log('Error while getting ecu Ids of data names.\nError: ' + error);
        } finally {
            return response;
        }
    }

    private getEcuIdFromDataName(dataName: string): number {
        // Regex to look for a hex number in the function name, schema like *___ID_78->* = 78
        const regex = /ID_([0-9A-Fa-f]+)->/;
        const match = dataName.match(regex);
        if (match && match[1]) {
            return parseInt(match[1], 16);
        } else {
            throw new Error('Could not get ID from dataName name: ' + dataName);
        }
    }

    private setDidMappingsOfEcuIds(ecuIds: number[]): Promise<void> {
        return new Promise(async (resolve, reject) => {
            for (let ecuId of ecuIds) {
                try {
                    await this.didConfigConnection.setEcuDidMapping(ecuId).then(() => {
                        resolve();
                    });
                } catch (error) {
                    console.log('Error while loading Mapping of ECU ID: ' + ecuId + '\nError: ' + error);
                }
            }
        });
    }


    public getDataNameOfIdentifier(identifier: t_DataIdentifier): string {
        let res = this.didConfigConnection.didMapping
            .find((ecu) => ecu.ecu_id == identifier.ecu_id)
            ?.ecu_dids.find((did) => did.did == identifier.data_id)?.did_name;
        if (res != '' && res != undefined) {
            return res;
        } else {
            return '';
        }
    }

    public getUnitOfIdentifier(identifier: t_DataIdentifier): string {
        let res = this.didConfigConnection.didMapping
            .find((ecu) => ecu.ecu_id == identifier.ecu_id)
            ?.ecu_dids.find((did) => did.did == identifier.data_id)?.unit;
        if (res != '' && res != undefined) {
            return res;
        } else {
            return '';
        }
    }

    public setCheckedStatus(functionName: string, identifier: t_DataIdentifier, checked: boolean): void {    
        let functionIndex: number = this.getIndexOfFunctionName(functionName);
        let dataIndex: number = this.getIndexOfDataIdentifierInFunction(functionIndex, identifier);

        this.basicMachineFunctionsConfig.functions[functionIndex].dataNames[dataIndex].checked = checked;
    }

    public getCheckedStatus(functionName: string, identifier: t_DataIdentifier): boolean {
        let functionIndex: number = this.getIndexOfFunctionName(functionName);
        let dataIndex: number = this.getIndexOfDataIdentifierInFunction(functionIndex, identifier);
        let checkedStatus;
        if(this.basicMachineFunctionsConfig.functions[functionIndex].dataNames[dataIndex] != undefined){
            checkedStatus = this.basicMachineFunctionsConfig.functions[functionIndex].dataNames[dataIndex].checked;
        }
        if (checkedStatus == undefined) {
            checkedStatus = false;
        }
        return checkedStatus;
    }

    /**
     * Test-System https://sentrack.staging.com2m.io/...
     * 
     * Produktiv-System https://sentrack.sennebogen.com/...
     *
     */

    // more than 5 possible
    public getMachine(value: string, token: string){
        let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.get(this.sentrackUrl + "/asset-service/items/Machine?filter.serialNumber=~(?i)" + value + "&filter.senconVersion=3&size=15&page=0&depth=0&expand=type&includeArchived=true", {
            observe: 'response',
            responseType: 'json',
            headers: headers,
        });
    }

    async getToken(){
        let token = await this.keycloak.getToken();
        this.token = token;   
    }

    public getExchangeToken(){
        this.getToken();
        let body = {'idToken': this.keycloak.getKeycloakInstance().idToken };
        return this.http.post(this.sentrackUrl + "/user-service/oauth/token/exchange", body, {
            observe: 'response',
            responseType: 'json',
        })
    }


    public getSWVersions(userkey: string, machineNumber: string){
        let param = {'machinenumber': machineNumber,'sentrackuserkey': userkey};
        return this.http.get(this.baseUrl + 'dataresource/swversions', {
            params: param,
            observe: 'response',
            responseType: 'json',           
        });
    }

    public getMachineOptions(line: string) {
        return this.http.get(this.baseUrl + 'machineoptions?productline=' + line + 'line', {
            observe: 'response',
            responseType: 'json',           
        });
    }

    public logDeliveryState(body: any) {
        return this.http.post(this.baseUrl + 'eol/deliverystate', body, {
            observe: 'response',
            responseType: 'json',
        });
    }

    public saveEolTestResults(body: any) {
        return this.http.post(this.baseUrl + 'eol/eol-test/result', body, {
            observe: 'response',
            responseType: 'json',
        });
    }

}

/* ================================================ */
/* ==================== TYPES ===================== */
/* ================================================ */

/**
 * *Basic Machine Functions Configuration*
 *
 * Basic Values for a specific function of the machine, like Pin/Boom
 * Type to store the complete configuration received from api. Contains an array with the config of the specific function
 */
export type t_BasicMachineFunctionsConfig = {
    functions: t_FunctionConfig[];
};

/**
 * *Function Config*
 *
 * Contains information for a single function. Includes name of the function, name of the corresponding data points and a flag if the identifiers are loaded.
 */
export type t_FunctionConfig = {
    name: string;
    dataNames: t_dataName[];
    identifierLoaded: boolean;
};

/**
 * *Data Name*
 *
 * Used to define the datapoints of a main function. Must contain the name of the datapoint.
 *
 * Optionally may contain the identifiier of the data or|and the whole did information (conversion, unit, etc.)
 *
 * Also has a checked flag to store if data is checked in the table overview
 */
export type t_dataName = {
    name: string;
    identifier?: t_DataIdentifier;
    didInformation?: t_Did;
    checked?: boolean;
};

export type t_Version = {
    name: string,
    version: string
}
